import { urlBase64ToUint8Array, isPushSupported } from "./push-utils";

export interface WebPushSubscriptionPayload {
  endpoint: string;
  p256dh: string;
  auth: string;
}

/**
 * ArrayBuffer'ı base64url string'e çevirir (subscribe payload için).
 */
function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = window.btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * İzin default ise kullanıcıya bildirim izni popup'ı gösterir.
 * Zaten granted veya denied ise mevcut değeri döndürür.
 */
export async function ensureNotificationPermission(): Promise<NotificationPermission> {
  if (!isPushSupported()) return "denied";
  const current = Notification.permission;
  if (current === "granted" || current === "denied") return current;
  const result = await Notification.requestPermission();
  return result;
}

export interface GetPushSubscriptionPayloadOptions {
  /**
   * true: İzin default ise tarayıcı popup'ı açar (banner'dan "Allow" ile kullan).
   * false: İzin sormaz, sadece mevcut permission'a bakar; granted değilse null döner (her login'de API'ye giderken kullan).
   */
  requestPermission?: boolean;
}

/**
 * Push aboneliği oluşturur ve backend'e gönderilecek payload'ı döndürür.
 * İzin granted değilse veya VAPID key yoksa null döner.
 * requestPermission false ise izin popup'ı açılmaz — backend'in "bu cihazda şu an bu kullanıcı" bilmesi için her login'de API çağrılmalı.
 */
export async function getPushSubscriptionPayload(
  options?: GetPushSubscriptionPayloadOptions
): Promise<WebPushSubscriptionPayload | null> {
  if (!isPushSupported()) return null;

  const requestPermission = options?.requestPermission !== false;
  const permission = requestPermission
    ? await ensureNotificationPermission()
    : Notification.permission;
  if (permission !== "granted") return null;

  // Service worker: önce mevcut registration, yoksa ready ile bekle (Chrome'da bazen gecikmeli)
  let swRegistration: ServiceWorkerRegistration | undefined =
    await navigator.serviceWorker?.getRegistration();
  if (!swRegistration) {
    try {
      swRegistration = await Promise.race([
        navigator.serviceWorker!.ready,
        new Promise<never>((_, rej) =>
          setTimeout(() => rej(new Error("sw_timeout")), 5000)
        ),
      ]);
    } catch {
      return null; // dev mode veya SW henüz yüklenmedi
    }
  }
  if (!swRegistration) return null;

  const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (!vapidPublicKey) {
    // VAPID key not set
    return null;
  }

  const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

  try {
    // Mevcut subscription varsa ve aynı VAPID key ile oluşturulmuşsa yeniden kullan.
    // Chrome'da unsubscribe + subscribe ardışık çağrısı race condition / hata verebiliyor.
    const existingSub = await swRegistration.pushManager.getSubscription();
    if (existingSub) {
      const opts = existingSub.options;
      const existingKey = opts?.applicationServerKey;
      const sameKey =
        existingKey &&
        existingKey.byteLength === applicationServerKey.length &&
        new Uint8Array(existingKey).every((b, i) => b === applicationServerKey[i]);
      if (sameKey) {
        const p256dh = existingSub.getKey("p256dh");
        const auth = existingSub.getKey("auth");
        if (p256dh && auth) {
          return {
            endpoint: existingSub.endpoint,
            p256dh: arrayBufferToBase64Url(p256dh),
            auth: arrayBufferToBase64Url(auth),
          };
        }
      }
      await existingSub.unsubscribe();
    }

    const subscription = await swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
    const p256dh = subscription.getKey("p256dh");
    const auth = subscription.getKey("auth");
    if (!p256dh || !auth) return null;
    return {
      endpoint: subscription.endpoint,
      p256dh: arrayBufferToBase64Url(p256dh),
      auth: arrayBufferToBase64Url(auth),
    };
  } catch (err) {
    // subscribe failed
    return null;
  }
}
